// src/components/outputs/DownloadMenu.jsx
//
// The «Scarica» dropdown of the console's run detail (admin/pages/RunDetailPage.jsx;
// the generator's card copies instead of downloading): the rows of
// downloadOptionsForEntry(entry) (src/publish/downloads.js) in three captioned
// groups, in catalogue order. A row calls onSelect(id) with the catalogue id;
// a mousedown outside `containerRef` calls onClose(). The component is only
// the popover: the caller owns the trigger button, the open state and the
// positioned wrapper that `containerRef` points at. An empty group prints
// nothing.
//
// Props: { options, onSelect, onClose, containerRef, t }
//   options       [{ id, label, group }] — group is 'ready' | 'all' | 'extra'
//   t             the caller's theme object (inputBg, border, text, textMuted,
//                 surface), so the menu matches a dark generator card and the
//                 console's light page alike.
// Captions: outputs:downloads.groups.<group> (the option labels live next to
// them, outputs:downloads.<id>).
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, FONT_MONO, RADII, SHADOW } from '../../constants/theme.js';

const DOWNLOAD_GROUPS = ['ready', 'all', 'extra'];

const captionStyle = (t) => ({
  fontSize: '10px',
  fontFamily: FONT_MONO,
  color: t.textMuted || COLORS.textTertiary,
  letterSpacing: '0.8px',
  textTransform: 'uppercase',
  padding: '6px 14px 2px',
});

const DownloadMenu = ({ options, onSelect, onClose, containerRef, t }) => {
  const { t: tr } = useTranslation('outputs');
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, containerRef]);

  const groups = DOWNLOAD_GROUPS
    .map((group) => ({ group, rows: (options || []).filter((o) => o.group === group) }))
    .filter(({ rows }) => rows.length > 0);

  return (
    <div role="menu" style={{
      position: 'absolute', top: '100%', right: 0, marginTop: '4px', zIndex: 100,
      background: t.inputBg || COLORS.surface, border: `1px solid ${t.border}`,
      borderRadius: RADII.controlInner, boxShadow: SHADOW.popover,
      overflow: 'hidden', minWidth: '220px', padding: '4px 0',
    }}>
      {groups.map(({ group, rows }, gi) => (
        <div key={group} style={gi > 0 ? { borderTop: `1px solid ${t.border}`, marginTop: '4px', paddingTop: '4px' } : undefined}>
          <div style={captionStyle(t)}>
            {tr(`downloads.groups.${group}`)}
          </div>
          {rows.map((option) => (
            <button
              key={option.id}
              type="button"
              role="menuitem"
              onClick={() => onSelect(option.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                padding: '8px 14px', border: 'none', background: 'transparent',
                color: t.text, fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = t.surface || COLORS.field; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              {option.label}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
};

export default DownloadMenu;
